import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconKrzyzyk } from "../icons/index.js";

/**
 * TO-1..TO-6: toast "szklany".
 *
 * Wartosci wygladu 1:1 z gotowca toast.html (promien 15px, blur(3px), wjazd 1000 ms
 * z translateY(-50px), czas zycia 3000 ms, top-center, stos maks. 3, tokeny
 * --toast-glass-*).
 *
 * DWA swiadome odstepstwa od gotowca, oba na korzysc kanonu:
 *
 * 1. PRZYCISK ZAMKNIECIA (TO-6) jest, mimo ze gotowiec go nie ma i pisze "znikaja
 *    same". To regres samego gotowca - "x" byl w tym pliku przed commitem 1b95d31.
 *    Zasada "kod gotowca > komentarz w gotowcu" rozstrzyga konflikt WEWNATRZ jednego
 *    pliku, a nie spor gotowiec kontra regula. TO-6 mowi wprost: obowiazkowy, bo toast
 *    stoi NAD headerem i przykrywa tresc.
 * 2. BRAK aria-live NA KONTENERZE. Gotowiec daje aria-live="polite" stosowi i
 *    jednoczesnie role="status"/"alert" kazdemu toastowi - czytnik ekranu ogloszlby
 *    tresc DWA RAZY. TO-5 przypisuje role samemu toastowi, wiec kontener zostaje niemy.
 *
 * NIE portujemy toastow z data-table.html ani pipeline.html - to wzorzec sprzed
 * TO-2/TO-3 (karta w rogu zamiast szklanej pigulki na gorze).
 */

export type WariantToastu = "success" | "danger" | "warning" | "info";

/** TO-4: JEDEN czas dla wszystkich wariantow. Wartosc DURATION z gotowca. */
const CZAS_ZYCIA = 3000;

/** TO-5: wyjazd trwa tyle, co wjazd. Po tym czasie toast znika z DOM. */
const CZAS_WYJSCIA = 1000;

/** TO-3: maksymalnie trzy naraz. */
const MAKS = 3;

interface Wpis {
  id: number;
  wariant: WariantToastu;
  tresc: string;
  wychodzi: boolean;
}

interface ApiToastow {
  pokaz(tresc: string, wariant?: WariantToastu): void;
  sukces(tresc: string): void;
  blad(tresc: string): void;
  ostrzezenie(tresc: string): void;
  informacja(tresc: string): void;
}

const Kontekst = createContext<ApiToastow | null>(null);

function ograniczonyRuch(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [wpisy, setWpisy] = useState<Wpis[]>([]);
  const licznikRef = useRef(0);
  const timeryRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const anulujTimer = useCallback((id: number) => {
    const t = timeryRef.current.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      timeryRef.current.delete(id);
    }
  }, []);

  const usun = useCallback(
    (id: number) => {
      anulujTimer(id);
      setWpisy((lista) => lista.filter((w) => w.id !== id));
    },
    [anulujTimer],
  );

  const zamknij = useCallback(
    (id: number) => {
      anulujTimer(id);
      if (ograniczonyRuch()) {
        usun(id);
        return;
      }
      setWpisy((lista) => lista.map((w) => (w.id === id ? { ...w, wychodzi: true } : w)));
      // Czekamy stalym timerem, nie zdarzeniem animationend: element, ktory nie
      // dostal klatek (karta w tle, tryb testowy), nigdy by go nie wyemitowal
      // i toast zostalby na ekranie na zawsze.
      timeryRef.current.set(
        id,
        setTimeout(() => usun(id), CZAS_WYJSCIA),
      );
    },
    [anulujTimer, usun],
  );

  const zaplanuj = useCallback(
    (id: number) => {
      anulujTimer(id);
      timeryRef.current.set(
        id,
        setTimeout(() => zamknij(id), CZAS_ZYCIA),
      );
    },
    [anulujTimer, zamknij],
  );

  const pokaz = useCallback(
    (tresc: string, wariant: WariantToastu = "info") => {
      licznikRef.current += 1;
      const id = licznikRef.current;
      setWpisy((lista) => {
        // TO-3: nowy jest najblizej krawedzi ekranu, czyli na poczatku listy.
        const nowa = [{ id, wariant, tresc, wychodzi: false }, ...lista];
        // Nadmiarowe (najstarsze) znikaja OD RAZU, bez animacji wyjscia. Gotowiec
        // animuje je, przez co przez sekunde na ekranie stoja cztery - a TO-3 mowi
        // "maksymalnie 3 naraz".
        for (const nadmiarowy of nowa.slice(MAKS)) anulujTimer(nadmiarowy.id);
        return nowa.slice(0, MAKS);
      });
      zaplanuj(id);
    },
    [anulujTimer, zaplanuj],
  );

  useEffect(() => {
    const timery = timeryRef.current;
    return () => {
      for (const t of timery.values()) clearTimeout(t);
      timery.clear();
    };
  }, []);

  const api = useMemo<ApiToastow>(
    () => ({
      pokaz,
      sukces: (t) => pokaz(t, "success"),
      blad: (t) => pokaz(t, "danger"),
      ostrzezenie: (t) => pokaz(t, "warning"),
      informacja: (t) => pokaz(t, "info"),
    }),
    [pokaz],
  );

  return (
    <Kontekst.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-stack">
          {wpisy.map((w) => (
            <div
              key={w.id}
              className={`toast${w.wychodzi ? " is-leaving" : ""}`}
              data-variant={w.wariant}
              // TO-5: blad i ostrzezenie przerywaja czytnikowi, sukces i informacja nie.
              role={w.wariant === "danger" || w.wariant === "warning" ? "alert" : "status"}
              onMouseEnter={() => anulujTimer(w.id)}
              onMouseLeave={() => {
                if (!w.wychodzi) zaplanuj(w.id);
              }}
            >
              <span className="toast__text">{w.tresc}</span>
              <button
                type="button"
                className="toast__close"
                aria-label="Zamknij powiadomienie"
                onClick={() => zamknij(w.id)}
              >
                <IconKrzyzyk size={16} />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Kontekst.Provider>
  );
}

/** TO-1: krotkie potwierdzenie ZDARZENIA. Nie do walidacji pol, stanow ani decyzji. */
export function useToast(): ApiToastow {
  const kontekst = useContext(Kontekst);
  if (!kontekst) throw new Error("useToast wymaga ToastProvider nad drzewem widoku.");
  return kontekst;
}
